import type { ComponentProps, ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';
import { useFieldContext } from './field-context';

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, className, id, ...rest }: CheckboxProps) {
  const field = useFieldContext();
  const auto = useId();
  const inputId = id ?? field?.id ?? auto;
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <input
        id={inputId}
        type="checkbox"
        aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
        className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-border-strong accent-primary disabled:cursor-not-allowed"
        {...rest}
      />
      <label htmlFor={inputId} className="cursor-pointer text-sm text-fg select-none">
        <span className="font-medium">{label}</span>
        {description && <span className="block text-xs text-fg-subtle">{description}</span>}
      </label>
    </div>
  );
}

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** role="switch" toggle. Space/Enter toggle (native button). Use for on/off settings that apply immediately or on save. */
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  id,
  className,
}: SwitchProps) {
  const auto = useId();
  const labelId = `${id ?? auto}-label`;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <div id={labelId} className="text-sm font-medium text-fg">
          {label}
        </div>
        {description && <div className="text-xs text-fg-subtle">{description}</div>}
      </div>
      <button
        id={id ?? auto}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-primary' : 'bg-border-strong',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'inline-block size-5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
    </div>
  );
}
